import React, { useRef, useCallback, useEffect, useState } from 'react';
import { Box, ResponsiveSection } from '@stitch-fix/mode-react';
import { LazyMotion, m } from 'framer-motion';
import { useMeasure } from 'react-use';
import { type CloudinaryImageFields } from '../CloudinaryImageWrapper';
import { useInfiniteScrollAnimation } from './useInfiniteScrollAnimation';
import { useScrollInteractions } from './useScrollInteractions';
import styles from './styles.module.scss';
import { ModuleWrapper, type ModuleWrapperProps } from '../ModuleWrapper';
import { CloudinaryImageV2, type ContentStackCloudinaryAssetProps } from '../CSCloudinaryAsset';
import { ModularComponentProps } from '../ModularPageWrapper/ModularPageWrapper';
type InteractiveImageScrollerBackgroundColor = 'white' | 'gray-99' | 'mint-60' | 'blue-45' | 'forest-60' | 'grapefruit-60' | 'citrus-60';
export type InteractiveImageScrollerProps = {
  images: CloudinaryImageFields[];
  speed?: number;
  gap?: number;
  imageProps?: Omit<ContentStackCloudinaryAssetProps['imageProps'], 'draggable'>;
  backgroundColor: InteractiveImageScrollerBackgroundColor;
  isScrollerHero?: boolean;
} & Omit<ModuleWrapperProps, 'children'>;
export const InteractiveImageScroller = ({
  images,
  alignment,
  moduleTitle,
  moduleFooter,
  verticalSpacing,
  backgroundColor = 'white',
  speed = 50,
  gap = 0,
  imageProps = {},
  isScrollerHero = false
}: InteractiveImageScrollerProps & ModularComponentProps) => {
  const [measureRef, {
    width: containerWidth = 0
  }] = useMeasure<HTMLDivElement>();
  const defaultSlotWidth = 340;
  const [imageWidth, setImageWidth] = useState(defaultSlotWidth);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const animatedDivRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const containerElement = containerRef.current;
    if (!containerElement) {
      return;
    }
    // use the CSS variable to get the image slot width to avoid CLS from JS breakpoint/SSR mismatch
    const imageSlotWidth = getComputedStyle(containerElement).getPropertyValue('--image-width').trim();
    const parsedImageSlotWidth = parseInt(imageSlotWidth, 10);
    if (!Number.isNaN(parsedImageSlotWidth)) {
      setImageWidth(parsedImageSlotWidth);
    }
  }, [containerWidth]);
  const setContainerRef = useCallback((el: HTMLDivElement | null) => {
    if (el) {
      measureRef(el);
      containerRef.current = el;
    }
  }, [measureRef]);
  const {
    animationProps,
    scrollableImages,
    imageSetWidth,
    isPaused,
    capturePositionAndPause
  } = useInfiniteScrollAnimation({
    images,
    imageWidth,
    gap,
    speed,
    animatedDivRef,
    containerWidth
  });
  const {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    isDraggingRef
  } = useScrollInteractions({
    containerRef,
    imageSetWidth,
    isPaused,
    capturePositionAndPause
  });
  let cursor: 'default' | 'grabbing' | 'grab';
  if (isDraggingRef.current) {
    cursor = 'grabbing';
  } else {
    cursor = 'grab';
  }
  return <Box data-testid="interactive-image-scroller-container" bgColor={backgroundColor} mt={verticalSpacing}>
      <ResponsiveSection width="xxlarge" gutter="none">
        <ModuleWrapper alignment={alignment} moduleTitle={moduleTitle} moduleFooter={moduleFooter} gutter="standard" includeYPadding={backgroundColor !== 'white' && !isScrollerHero}>
          <LazyMotion features={() => import('framer-motion').then(mod => mod.domAnimation)}>
            <Box ref={setContainerRef} className={styles.container} style={{
            overflowX: 'hidden',
            // prevent native scroll/pan so pointer events drive custom scroll on touch
            touchAction: 'none',
            cursor
          }} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp} onPointerLeave={handlePointerUp}>
              <m.div ref={animatedDivRef} initial={{
              x: 0
            }} {...animationProps} style={{
              display: 'flex',
              gap: `${gap}px`,
              width: 'fit-content'
            }}>
                {scrollableImages.map((image, index) => {
                if (!image.cloudinaryAsset) {
                  return null;
                }
                const setIndex = Math.floor(index / images.length);
                const imageIndex = index % images.length;
                return <Box className={styles.imageSlot}
                // unique key combining source, set, and position
                key={`${image.src}-${setIndex}-${imageIndex}`} style={{
                  // when we are not using a gap we need to set a negative
                  // margin to prevent a hairline gap from appearing because
                  // of the subpixel math
                  ...(gap === 0 && {
                    marginRight: -1
                  })
                }}>
                      <CloudinaryImageV2 alt={image.alt} cloudinaryAsset={image.cloudinaryAsset} sizes={[{
                    minWidth: imageWidth,
                    staticWidth: imageWidth
                  }]} imageProps={{
                    // prevent native image drag
                    draggable: false,
                    loading: 'lazy',
                    ...imageProps
                  }} />
                    </Box>;
              })}
              </m.div>
            </Box>
          </LazyMotion>
        </ModuleWrapper>
      </ResponsiveSection>
    </Box>;
};